import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import Button from "./Button";

/**
 * Reusable right-side drawer for the leg command card. Slides in from the
 * right over a dim backdrop while the page stays visible behind it, so a
 * dispatcher can open one collection (deployments, incidents, expenses, …)
 * without leaving the leg they are monitoring.
 *
 * Rendered through a portal to document.body so it overlays everything
 * regardless of where it is mounted in the tree. Closes on the ✕ icon, a
 * backdrop click, or the Escape key. Renders nothing while closed.
 */
interface SideDrawerProps {
    open: boolean;
    title: string;
    onClose: () => void;
    width?: number;
    children: React.ReactNode;
    headerExtra?: React.ReactNode;
    /** The drawer's main action, pinned at the bottom right (stays in view while the body scrolls). */
    footer?: React.ReactNode;
}

const SideDrawer: React.FC<SideDrawerProps> = ({
    open,
    title,
    onClose,
    width = 880,
    children,
    headerExtra,
    footer,
}) => {
    // Close on Escape while open; listener is added/removed with the drawer so
    // a closed drawer never intercepts keystrokes meant for the page behind it.
    useEffect(() => {
        if (!open) {
            return;
        }
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, onClose]);

    if (!open) {
        return null;
    }

    return createPortal(
        <>
            <div className="side-drawer-backdrop" onClick={onClose} />
            <div
                className="side-drawer"
                // Callers request desktop widths up to 1500px; on any viewport
                // narrower than that the drawer must clamp to the screen, not
                // push content off it.
                style={{ width: `min(${width}px, 100vw)` }}
                role="dialog"
                aria-modal="true"
                aria-label={title}
            >
                <div className="side-drawer-header">
                    <div className="side-drawer-header-main">
                        <span className="side-drawer-title">{title}</span>
                        {headerExtra}
                    </div>
                    <Button type="button" variant="ghost" size="sm" icon="x-lg" className="side-drawer-close" onClick={onClose}>
                        Close
                    </Button>
                </div>
                <div className="side-drawer-body">{children}</div>
                {footer && <div className="side-drawer-footer">{footer}</div>}
            </div>
        </>,
        document.body
    );
};

export default SideDrawer;
